import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2 } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BUDGETS = ["Under $500", "$500 – $2,000", "$2,000 – $5,000", "$5,000 – $10,000", "$10,000+", "Not sure yet"];
const EMPTY = { company: "", contact_name: "", email: "", budget: "", goals: "" };
const field = "h-12 rounded-2xl border-2 border-ink bg-white text-[15px]";

const Label = ({ children }) => (
  <span className="block text-[12px] font-bold tracking-[0.15em] uppercase text-ink/70 mb-2">{children}</span>
);

const EnquiryForm = ({ onSent }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.budget) return toast.error("Please pick a budget range");
    if (form.goals.trim().length < 10) return toast.error("Tell us a little more about your campaign goals");
    setBusy(true);
    try {
      await axios.post(`${API}/brand-enquiry`, form);
      setForm(EMPTY);
      onSent();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? "Please check your details (e.g. a valid email)" : d || "Something went wrong. Please email WPAAgency2026@gmail.com");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="brand-enquiry-form">
      <label><Label>Company</Label>
        <Input required value={form.company} onChange={set("company")} className={field} data-testid="enquiry-company" /></label>
      <label><Label>Your name</Label>
        <Input required value={form.contact_name} onChange={set("contact_name")} className={field} data-testid="enquiry-name" /></label>
      <label><Label>Work email</Label>
        <Input required type="email" value={form.email} onChange={set("email")} className={field} data-testid="enquiry-email" /></label>
      <div><Label>Budget</Label>
        <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
          <SelectTrigger className={field} data-testid="enquiry-budget"><SelectValue placeholder="Pick a range" /></SelectTrigger>
          <SelectContent>
            {BUDGETS.map((b) => <SelectItem key={b} value={b} data-testid={`enquiry-budget-${BUDGETS.indexOf(b)}`}>{b}</SelectItem>)}
          </SelectContent>
        </Select></div>
      <label className="sm:col-span-2"><Label>Campaign goals</Label>
        <Textarea required rows={5} maxLength={2000} value={form.goals} onChange={set("goals")}
          placeholder="What are you promoting, who do you want to reach, and when?"
          className="rounded-2xl border-2 border-ink bg-white text-[15px]" data-testid="enquiry-goals" /></label>
      <div className="sm:col-span-2">
        <button type="submit" disabled={busy} data-testid="enquiry-submit"
          className="inline-flex items-center gap-2 bg-pink text-white px-8 py-4 rounded-full font-semibold text-[15px] hover:bg-ink transition-colors disabled:opacity-60">
          <Send size={16} /> {busy ? "Sending..." : "Send enquiry"}
        </button>
      </div>
    </form>
  );
};

export const BrandEnquiry = () => {
  const [sent, setSent] = useState(false);
  return (
    <section id="brand-enquiry" className="relative bg-lime">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="text-[12px] font-bold tracking-[0.2em] uppercase text-ink/70 mb-6">For brands</p>
          <h2 className="font-display text-ink leading-[1]" style={{ fontSize: "clamp(40px, 6vw, 84px)" }}>
            Find Creators for your <span className="text-pink">Brand.</span>
          </h2>
          <p className="mt-6 text-ink/80 text-[17px] leading-relaxed max-w-[48ch]">
            Tell us about your campaign and we'll match you with trained WPA creators whose audiences fit your goals. Fees are determined once you share your campaign scope.
          </p>
        </div>
        <div className="lg:col-span-7 bg-cream rounded-[32px] border-4 border-ink shadow-pop p-6 sm:p-10">
          {sent ? (
            <div className="flex flex-col items-start gap-4 py-10" data-testid="enquiry-success">
              <CheckCircle2 size={44} className="text-pink" />
              <h3 className="font-display text-ink text-[32px] leading-tight">Enquiry sent — thank you!</h3>
              <p className="text-ink/75 text-[16px]">We've emailed you a confirmation. The WPA team will get back to you within 2 business days.</p>
              <button onClick={() => setSent(false)} className="underline font-semibold text-ink hover:text-pink" data-testid="enquiry-another">
                Send another enquiry
              </button>
            </div>
          ) : (
            <EnquiryForm onSent={() => setSent(true)} />
          )}
        </div>
      </div>
    </section>
  );
};
